import {
    isConnectedDeviceUserAgent,
    isConnectedTVUserAgent,
    isPhoneDeviceUserAgent,
    isSetTopBoxUserAgent,
    isTabletDeviceUserAgent,
} from '../tracking/getDeviceType';
import { isTouchMacUserAgent } from '../user-agent/isTabletUserAgent';

import { hasPhoneLikeInputSignals } from './getDevicePlatform';

export type WebDeviceModel = 'desktop' | 'mobile' | 'tablet';

/**
 * Maximum character length to inspect for user-agent evaluation to guard against ReDoS or
 * pathological regular expression backtracking on oversized incoming headers.
 */
const MAX_USER_AGENT_LENGTH = 500;

export function isWebDeviceModel(value: unknown): value is WebDeviceModel {
    return value === 'desktop' || value === 'mobile' || value === 'tablet';
}

/**
 * Chrome and Samsung Internet send `Mobile` on phones and omit it on tablets, so its absence is the
 * most reliable Android tablet signal there is. `getDeviceType` deliberately does not use it — it
 * recognises tablets by model code, which only covers Samsung and Lenovo — but this taxonomy feeds
 * ad targeting, where sending every other vendor's tablet as a phone is the more costly error.
 * Only reachable once TVs, boxes, and streaming devices have been claimed.
 */
function isAndroidTabletUserAgent(userAgent: string): boolean {
    // Tested as two conditions rather than one `android(?!.*mobile)` lookahead: in-app browsers
    // append a second `Android` token after `Mobile`, and at that trailing position the lookahead
    // has no `Mobile` left to veto it, so a phone would read as a tablet.
    return /\bandroid\b/i.test(userAgent) && !/\bmobile\b/i.test(userAgent);
}

/** `KF*` model codes are Kindle Fire tablets whether or not Silk sends a `Mobile` token. */
function isKindleFireUserAgent(userAgent: string): boolean {
    return /\bkf[a-z0-9]+\b/i.test(userAgent);
}

/**
 * Derives the device model ('desktop' | 'mobile' | 'tablet') for FreeWheel and Stitcher AdTech
 * reporting from a User-Agent string or client browser signals.
 *
 * @param userAgent - Optional raw User-Agent string or array of headers (used in SSR / request contexts)
 * @param maxTouchPoints - Optional navigator.maxTouchPoints (used to detect iPadOS desktop-mode Safari)
 * @returns 'desktop' | 'mobile' | 'tablet'
 */
export function getDeviceModel(
    userAgent?: string | string[],
    maxTouchPoints?: number,
): WebDeviceModel {
    let raw = Array.isArray(userAgent) ? userAgent[0] : userAgent;
    let touchPoints = maxTouchPoints;
    // Only a navigator-derived UA describes the device whose input signals we can read locally.
    let describesLocalDevice = false;

    // Navigator UA and navigator touch points are read as a pair: pulling local touch points in
    // alongside a caller-supplied (request-header) UA would classify from two different devices.
    if (raw === undefined && typeof navigator !== 'undefined') {
        raw = navigator.userAgent;
        touchPoints ??= navigator.maxTouchPoints ?? 0;
        describesLocalDevice = true;
    }

    if (!raw || typeof raw !== 'string') {
        return 'desktop';
    }

    const clampedUa = raw.slice(0, MAX_USER_AGENT_LENGTH);
    const resolvedTouchPoints = touchPoints ?? 0;

    // This taxonomy has no TV value, so a TV, box, or streaming stick takes the 'desktop' fallback.
    // They are matched first because many run Android or carry a `Mobile` token and would otherwise
    // be read as phones — the same precedence `getDeviceType` applies.
    if (
        isSetTopBoxUserAgent(clampedUa) ||
        isConnectedTVUserAgent(clampedUa) ||
        isConnectedDeviceUserAgent(clampedUa)
    ) {
        return 'desktop';
    }

    // Tablet before phone: an iPad sends `Mobile` and an Android tablet sends `android`, so the
    // phone rule below would otherwise claim both.
    if (
        isTabletDeviceUserAgent(clampedUa, resolvedTouchPoints) ||
        isAndroidTabletUserAgent(clampedUa) ||
        isKindleFireUserAgent(clampedUa)
    ) {
        // A phone in desktop-site mode presents the same Macintosh + multi-touch UA as an iPad, so
        // only client input signals separate them. No-ops server-side, leaving iPad UAs as tablets.
        if (isTouchMacUserAgent(clampedUa, resolvedTouchPoints) && hasPhoneLikeInputSignals()) {
            return 'mobile';
        }
        return 'tablet';
    }

    // A bare `Mobile` token is enough here: tablets and TVs are already claimed above, so the only
    // remaining devices that advertise it are phones (KaiOS and BlackBerry name no other platform).
    if (isPhoneDeviceUserAgent(clampedUa) || /\bmobile\b/i.test(clampedUa)) {
        return 'mobile';
    }

    // Android desktop-site mode reports a bare `X11; Linux x86_64` UA with no device token at all,
    // so input signals are the only remaining way to tell that phone from a Linux desktop.
    if (describesLocalDevice && hasPhoneLikeInputSignals()) {
        return 'mobile';
    }

    return 'desktop';
}
